import React from 'react';
import './rush.css';
import './features.css';
import './feature-detail.css';
import { RushNav, RushFooter, useRushReveal } from './RushChrome';
import { FI, FeatHero, FeatCapabilities, FeatDetailRow, FeatSteps, FeatQuote, FeatCrossLinks, FeatFinalCTA } from './RushFeatureLayout';

const AUNode = ({ k, ic, title, sub, accent }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '11px 13px', borderRadius: 11, background: accent ? 'color-mix(in oklab, var(--indigo) 7%, var(--bg-card))' : 'var(--bg-soft)', border: `1px solid ${accent ? 'color-mix(in oklab, var(--indigo) 30%, var(--line))' : 'var(--line)'}` }}>
    <span style={{ width: 32, height: 32, borderRadius: 9, background: 'var(--bg-card)', border: '1px solid var(--line)', color: accent ? 'var(--indigo)' : 'var(--ink-3)', display: 'grid', placeItems: 'center', flexShrink: 0 }}>{ic}</span>
    <div style={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
      <span style={{ fontSize: 9.5, fontWeight: 700, letterSpacing: '.06em', color: 'var(--ink-4)' }}>{k}</span>
      <b style={{ fontSize: 13 }}>{title}</b>
      <span style={{ fontSize: 11.5, color: 'var(--ink-4)' }}>{sub}</span>
    </div>
  </div>
);
const AUConnector = () => <div style={{ width: 1, height: 13, background: 'var(--line-2)', marginLeft: 28 }}></div>;

const AUHeroVisual = () => (
  <div className="mock-frame">
    <div className="mock-chrome"><span className="mc-dot"></span><span className="mc-dot"></span><span className="mc-dot"></span><span className="mc-title">Localize on every PR · Active</span></div>
    <div style={{ padding: 18, display: 'flex', flexDirection: 'column', gap: 0 }}>
      <AUNode k="WHEN" ic={FI.flow} title="Pull request opened" sub="acme/strings" />
      <AUConnector />
      <div style={{ marginLeft: 28, marginBottom: 0 }}>
        <span style={{ display: 'inline-flex', alignItems: 'center', fontSize: 11, fontWeight: 600, color: 'var(--ink-3)', background: 'var(--bg-soft)', border: '1px solid var(--line)', borderRadius: 999, padding: '3px 11px' }}>If file type is JSON</span>
      </div>
      <AUConnector />
      <AUNode k="THEN" ic={FI.ai} title="Translate" sub="into DE · FR · JA · ES" accent />
      <AUConnector />
      <AUNode k="THEN" ic={FI.flow} title="Open translation PR" sub="as a follow-up PR" accent />
      <div style={{ display: 'flex', gap: 14, marginTop: 16, paddingTop: 14, borderTop: '1px solid var(--line)', fontSize: 11.5, color: 'var(--ink-3)' }}>
        <span>Ran <b style={{ color: 'var(--ink)' }}>142</b> times</span>
        <span>·</span>
        <span>Saved <b style={{ color: 'var(--ink)' }}>38 hrs</b> this month</span>
        <span style={{ marginLeft: 'auto', color: 'var(--green)', fontWeight: 600 }}>99.4% success</span>
      </div>
    </div>
  </div>
);

const AURecipesVisual = () => (
  <div className="mock-frame">
    <div className="mock-chrome"><span className="mc-dot"></span><span className="mc-dot"></span><span className="mc-dot"></span><span className="mc-title">Start from a recipe</span></div>
    <div style={{ padding: 14, display: 'flex', flexDirection: 'column', gap: 8 }}>
      {[[FI.layers, 'Auto-route new projects', 'Route by language pair, notify the linguist.'],
      [FI.lock, 'Catch glossary slips', 'Block the approval, DM the assignee.'],
      [FI.bolt, 'Re-check on source drift', 'Re-translate when source text changes.'],
      [FI.history, 'Escalate rejected reviews', 'Reopen and ping the PM on rejection.']].map(([ic, t, d]) => (
        <div key={t} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '11px 12px', borderRadius: 10, border: '1px solid var(--line)', background: 'var(--bg-soft)' }}>
          <span style={{ width: 30, height: 30, borderRadius: 8, background: 'var(--indigo-soft)', color: 'var(--indigo)', display: 'grid', placeItems: 'center', flexShrink: 0 }}>{ic}</span>
          <div style={{ minWidth: 0 }}>
            <b style={{ fontSize: 12.5, display: 'block' }}>{t}</b>
            <span style={{ fontSize: 11.5, color: 'var(--ink-4)' }}>{d}</span>
          </div>
          <span style={{ marginLeft: 'auto', color: 'var(--ink-4)', flexShrink: 0 }}>{FI.arrowR}</span>
        </div>
      ))}
    </div>
  </div>
);

const RushFeatureAutomations = () => {
  useRushReveal();
  return (
    <div className="rs">
      <RushNav active="features" />
      <FeatHero
        tint="indigo"
        kicker="Automations"
        kickerIcon={FI.flow}
        title="Localization that runs itself."
        lead="Turn workflow events and connected sources into pipelines. Segment approved, glossary slip, TM match drops, source text changes, new project created, deadline approaching — pick what fires it, and Rush Studio runs the steps for you. Real triggers wired to your real team, projects, glossaries, and integrations."
        primary={{ label: 'Get early access', to: '/register' }}
        secondary={{ label: 'See all features', to: '/features' }}
        meta={['25+ event triggers', 'Trigger-aware conditions', 'Connects to your team & projects', 'Full run history']}
        visual={<AUHeroVisual />}
      />

      <FeatCapabilities
        tint="indigo"
        eyebrow="What it does"
        title="Build pipelines, not tickets."
        lead="A trigger, optional conditions, and a chain of actions. No code, no glue scripts — just the work that used to live in someone's head."
        caps={[
          { icon: FI.bolt, title: 'Workflow triggers', desc: 'Segment approved, rejected in review, status changed, linguist assigned, batch submitted — fire on the moments that actually move a job.' },
          { icon: FI.flow, title: 'Project lifecycle', desc: 'Project created, language added, deadline approaching, project overdue, or 100% complete — keep stakeholders ahead of the curve.' },
          { icon: FI.lock, title: 'Quality & memory', desc: 'TM match below threshold, glossary term violated, AI quality drops, or source text changes after translation. Catch drift the moment it happens.' },
          { icon: FI.layers, title: 'Trigger-aware conditions', desc: "Filter by target language, project, client, assignee, urgency, word count, glossary hit — only the rules that make sense for the trigger you picked." },
          { icon: FI.flow, title: 'Real actions', desc: 'Translate, auto-assign to the default linguist for the pair, run glossary QA, open a translation PR, push strings to Figma, save to Drive, or DM the right person on Slack.' },
          { icon: FI.history, title: 'Runs, audit & re-run', desc: 'Every fire is logged with success rate, duration, and the failures spelled out. Re-run a failed run from the drawer once you fix the cause.' },
        ]}
      />

      <FeatDetailRow
        reverse
        eyebrow="Recipes"
        title="Start from a proven pattern."
        intro="Nine ready-made templates for the moments that actually move a localization team's day. Pick one, point it at a source, and it's live. Every recipe is just a starting point — open the builder and add conditions or actions any time."
        points={[
          { icon: FI.layers, title: 'Auto-route new projects', desc: 'A project is created → route to the right linguist for the language pair and notify them.' },
          { icon: FI.lock, title: 'Catch glossary slips', desc: 'A glossary term is violated → block the approval and DM the assignee with the locked rendering.' },
          { icon: FI.bolt, title: 'Re-check on source drift', desc: 'Source text changes after translation → re-translate the affected segments automatically.' },
          { icon: FI.history, title: 'Escalate rejected reviews', desc: 'A segment fails review → move it back to translation and notify the PM.' },
          { icon: FI.flow, title: 'Low-quality watchdog', desc: 'AI quality dips below 80 → assign for human review before it reaches the client.' },
        ]}
        replaces={['Manual triage', '"Did anyone translate this?" threads', 'Hand-checking glossary slips']}
        visual={<AURecipesVisual />}
      />

      <FeatDetailRow
        eyebrow="Connected to your workspace"
        title="The dropdowns aren't demo data."
        intro="Every option list is wired to the real workspace. Auto-assign picks from your actual team. Project conditions list your actual projects. Glossary QA points at the glossaries you've uploaded. Repository triggers list the repos you've linked. The picker is empty until the integration is connected — and tells you why."
        points={[
          { icon: FI.layers, title: 'Default linguist by language', desc: "Set who handles each target language once on the People page. Every 'Auto-assign → Default linguist for the pair' rule resolves to that person at run time." },
          { icon: FI.flow, title: 'Live integration awareness', desc: "Each trigger and action shows which integrations it depends on and whether they're connected — so a rule never silently no-ops." },
          { icon: FI.lock, title: 'Trigger-scoped conditions', desc: "Switch from 'File added' to 'Segment approved' and the IF picker swaps in the conditions that make sense — Folder out, Assignee in. Conditions that no longer apply are pruned automatically." },
        ]}
        replaces={['"Why didn\'t this rule fire?"', 'Demo-data drop-downs that map to nothing']}
      />

      <FeatSteps
        eyebrow="How it works"
        title="Three parts. Endless pipelines."
        steps={[
          { h: 'Pick a trigger', p: 'Choose from workflow events (segment approved, status changed), project lifecycle (new project, deadline near), quality (TM low, glossary slip, source drift), or any connected source.' },
          { h: 'Add conditions & actions', p: "Narrow by target language, project, client, assignee, urgency — only the conditions that fit the trigger appear. Chain the steps: translate, auto-assign, glossary QA, notify, export, or push." },
          { h: 'Turn it on', p: "Save and it's live. Watch runs accrue in the audit log, with success rate, duration, and re-run on any failure." },
        ]}
      />

      <FeatQuote quote="Our strings PR now opens its own translation PR. The localization step just isn't a manual task anymore." initials="TS" name="Tomás Silva" role="Staff engineer, Helios" />

      <FeatCrossLinks current="automations" />

      <FeatFinalCTA source="feature-automations" title="Put your localization on autopilot." sub="Join the waitlist for early-access pricing and a 1:1 onboarding call with our team." />

      <RushFooter />
    </div>
  );
};

export default RushFeatureAutomations;
